import http, { AUTH_ROUTES } from "../../../services/api";
import {
  errorNotifier,
  successNotifier,
} from "../../../components/NotificationHandler";

const REQUEST_TIMEOUT = 15000;

const pickValue = (...values) =>
  values.find((value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
  });

const extractProducts = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.products)) return payload.products;
  if (Array.isArray(payload?.vendorProducts)) return payload.vendorProducts;
  if (Array.isArray(payload?.foods)) return payload.foods;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.result?.data)) return payload.result.data;
  if (Array.isArray(payload?.result?.products)) return payload.result.products;

  return [];
};

const extractVendors = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.vendors)) return payload.vendors;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.result?.data)) return payload.result.data;
  if (Array.isArray(payload?.result?.vendors)) return payload.result.vendors;

  return [];
};

const extractOrders = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.orders)) return payload.orders;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.result?.data)) return payload.result.data;
  if (Array.isArray(payload?.result?.orders)) return payload.result.orders;

  return [];
};

const formatAmount = (value) => {
  const amount = Number.parseFloat(String(value ?? "").replace(/[^\d.-]/g, ""));

  if (!Number.isFinite(amount)) return "N/A";

  return `NGN ${amount.toLocaleString("en-NG", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const resolveImageUrl = (image) => {
  const imageValue = Array.isArray(image) ? image[0] : image;
  const rawUrl =
    typeof imageValue === "string"
      ? imageValue
      : pickValue(imageValue?.url, imageValue?.secure_url, imageValue?.path);

  if (!rawUrl) return "";

  const trimmedUrl = String(rawUrl).trim();
  if (/^(https?:|data:|blob:)/i.test(trimmedUrl)) return trimmedUrl;

  return trimmedUrl;
};

const normalizeFoodItem = (item = {}, index = 0) => {
  const rawAmount = pickValue(item?.price, item?.amount, item?.sellingPrice, item?.unitPrice);
  const available = pickValue(
    item?.isAvailable,
    item?.available,
    item?.inStock,
    item?.status,
    item?.availability
  );

  const availability =
    typeof available === "boolean"
      ? available
        ? "Available"
        : "Unavailable"
      : String(available || "Available");

  return {
    id: String(pickValue(item?._id, item?.id, item?.productId, `food-${index + 1}`)),
    vendorProductId: String(pickValue(item?._id, item?.id, item?.productId, "")),
    vendorId: String(
      pickValue(
        item?.vendor?._id,
        item?.vendor?.id,
        typeof item?.vendor === "string" ? item.vendor : undefined,
        item?.vendorId,
        item?.vendor_id,
        ""
      )
    ),
    name: String(
      pickValue(item?.name, item?.productName, item?.foodName, item?.title, `Food item ${index + 1}`)
    ),
    category: String(pickValue(item?.category?.name, item?.category, item?.type, "General")),
    description: String(pickValue(item?.description, item?.details, item?.summary, "")),
    location: String(pickValue(item?.location, item?.estate, item?.area, "N/A")),
    vendorName: String(
      pickValue(item?.vendor?.businessName, item?.vendor?.name, item?.vendorName, "Vendor")
    ),
    amount: rawAmount,
    price: formatAmount(rawAmount),
    image: resolveImageUrl(
      pickValue(item?.image, item?.imageUrl, item?.productImage, item?.foodImage, item?.photo)
    ),
    availability,
  };
};

const normalizeVendor = (vendor = {}, index = 0) => {
  const name = String(
    pickValue(
      vendor?.businessName,
      vendor?.companyName,
      vendor?.vendorName,
      vendor?.name,
      vendor?.fullName,
      `Vendor ${index + 1}`
    )
  );

  return {
    id: String(pickValue(vendor?._id, vendor?.id, vendor?.vendorId, name)),
    name,
  };
};

const normalizeFoodOrder = (order = {}, index = 0) => ({
  id: String(pickValue(order?._id, order?.id, `order-${index + 1}`)),
  productName: String(pickValue(order?.productName, order?.name, "Food order")),
  description: String(pickValue(order?.description, order?.details, order?.summary, "")),
  vendorName: String(pickValue(order?.vendorName, order?.vendor?.name, "Vendor")),
  quantity: pickValue(order?.quantity, 1),
  amount: formatAmount(order?.amount),
  totalAmount: formatAmount(order?.totalAmount),
  image: resolveImageUrl(
    pickValue(order?.imageUrl, order?.image, order?.productImage, order?.foodImage, order?.photo)
  ),
  paymentMethod: String(pickValue(order?.paymentMethod, "N/A")),
  paymentStatus: String(pickValue(order?.paymentStatus, order?.status, "N/A")),
  status: String(pickValue(order?.status, order?.paymentStatus, "N/A")),
  verified: Boolean(order?.verified),
  reference: String(pickValue(order?.paymentReference, order?.reference, "N/A")),
  createdAt: order?.createdAt,
});

export const fetchFoodListings = async (setLoading, setFoods, setError) => {
  setLoading(true);
  try {
    const response = await http.get(AUTH_ROUTES.GET_FOOD_LISTINGS, {
      params: { sort: "desc" },
      timeout: REQUEST_TIMEOUT,
    });

    const payload =
      response?.data?.result?.data ??
      response?.data?.data ??
      response?.data?.result ??
      response?.data ??
      [];
    const foods = extractProducts(payload).map(normalizeFoodItem);

    setFoods(foods);
    return foods;
  } catch (error) {
    console.error("Error fetching food listings:", error);
    setFoods([]);
    if (setError) {
      setError(
        error?.response?.data?.message || "Unable to load food listings right now."
      );
    }
    return [];
  } finally {
    setLoading(false);
  }
};

export const fetchFoodOrders = async (userId, setLoading, setOrders, setError) => {
  setLoading(true);
  try {
    const response = await http.get(AUTH_ROUTES.GET_PAID_VENDOR_PRODUCTS(userId), {
      timeout: REQUEST_TIMEOUT,
    });

    const payload =
      response?.data?.result?.data ??
      response?.data?.data ??
      response?.data?.result ??
      response?.data ??
      [];
    const orders = extractOrders(payload).map(normalizeFoodOrder);

    setOrders(orders);
    return orders;
  } catch (error) {
    console.error("Error fetching food orders:", error);
    setOrders([]);
    if (setError) {
      setError(error?.response?.data?.message || "Unable to load food orders right now.");
    }
    return [];
  } finally {
    setLoading(false);
  }
};

export const fetchVendors = async (setVendors) => {
  try {
    const response = await http.get(AUTH_ROUTES.GET_VENDORS, {
      timeout: REQUEST_TIMEOUT,
    });

    const payload =
      response?.data?.result?.data ??
      response?.data?.data ??
      response?.data?.result ??
      response?.data ??
      [];
    const vendors = extractVendors(payload).map(normalizeVendor);

    setVendors(vendors);
    return vendors;
  } catch (error) {
    console.error("Error fetching vendors:", error);
    setVendors([]);
    return [];
  }
};

export const payVendorProduct = async (payload, setLoading) => {
  setLoading(true);
  try {
    const response = await http.post(AUTH_ROUTES.PAY_VENDOR_PRODUCT, payload, {
      timeout: REQUEST_TIMEOUT,
    });

    successNotifier("Payment initialized successfully");
    return response?.data;
  } catch (error) {
    console.error("Error initializing vendor product payment:", error);
    errorNotifier(
      error?.response?.data?.message ||
        error?.response?.data?.result?.message ||
        "Unable to initialize payment right now."
    );
    return null;
  } finally {
    setLoading(false);
  }
};
